// src/components/messaging/ConversationItem.tsx
import React from 'react';
import {
  Box,
  ListItemButton,
  Avatar,
  Typography,
  Badge,
  alpha,
} from '@mui/material';
import { Conversation, Group } from '../../types/messaging';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';

interface ConversationItemProps {
  conversation?: Conversation;
  group?: Group;
  isSelected: boolean;
  isOnline?: boolean;
  onClick: () => void;
}

const ConversationItem: React.FC<ConversationItemProps> = ({
  conversation,
  group,
  isSelected,
  isOnline = false,
  onClick,
}) => {
  const isGroup = !!group;
  const name = isGroup ? group.name : conversation?.other_user?.name || 'Unknown';
  const avatarUrl = isGroup ? group.image_url : conversation?.other_user?.avatar_url;
  const lastMessage = isGroup ? group.last_message : conversation?.last_message;
  const unreadCount = isGroup ? (group.unread_count || 0) : (conversation?.unread_count || 0);

  // Get initials for avatar
  const getInitials = (name: string): string => {
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  };

  // Format time
  const formatTime = (dateString?: string | null): string => {
    if (!dateString) return '';
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: false, locale: fr });
    } catch {
      return '';
    }
  };

  // Truncate message
  const truncateMessage = (message?: string | null, maxLength: number = 40): string => {
    if (!message) return '';
    return message.length > maxLength ? `${message.slice(0, maxLength)}...` : message;
  };

  return (
    <ListItemButton
      onClick={onClick}
      sx={{
        py: 1.5,
        px: 2,
        borderRadius: 2,
        mx: 1,
        mb: 0.5,
        backgroundColor: isSelected ? (theme) => alpha(theme.palette.primary.main, 0.08) : 'transparent',
        borderLeft: isSelected ? '3px solid' : '3px solid transparent',
        borderLeftColor: isSelected ? 'primary.main' : 'transparent',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          backgroundColor: isSelected
            ? (theme) => alpha(theme.palette.primary.main, 0.12)
            : (theme) => alpha(theme.palette.grey[500], 0.08),
          transform: 'translateX(2px)',
        },
      }}
    >
      {/* Avatar with online indicator */}
      <Badge
        overlap="circular"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        variant="dot"
        sx={{
          '& .MuiBadge-badge': {
            backgroundColor: isOnline ? '#10b981' : 'transparent',
            color: '#10b981',
            boxShadow: isOnline ? '0 0 0 2px white' : 'none',
            width: 10,
            height: 10,
            borderRadius: '50%',
          },
        }}
      >
        <Avatar
          src={avatarUrl || undefined}
          sx={{
            width: 48,
            height: 48,
            fontSize: '1rem',
            fontWeight: 600,
            backgroundColor: isGroup ? 'secondary.main' : 'primary.main',
          }}
        >
          {getInitials(name)}
        </Avatar>
      </Badge>

      {/* Content */}
      <Box sx={{ ml: 2, flex: 1, minWidth: 0, overflow: 'hidden' }}>
        {/* Name and time row */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: unreadCount > 0 ? 700 : 500,
              color: 'text.primary',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {name}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: unreadCount > 0 ? 'primary.main' : 'text.secondary',
              fontWeight: unreadCount > 0 ? 600 : 400,
              fontSize: '0.7rem',
              ml: 1,
              flexShrink: 0,
            }}
          >
            {formatTime(lastMessage?.created_at)}
          </Typography>
        </Box>

        {/* Last message and badge row */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography
            variant="body2"
            sx={{
              color: unreadCount > 0 ? 'text.primary' : 'text.secondary',
              fontWeight: unreadCount > 0 ? 500 : 400,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              flex: 1,
            }}
          >
            {lastMessage?.deleted_at
              ? 'Message supprimé'
              : lastMessage?.type === 'image'
              ? '📷 Image'
              : lastMessage?.type === 'file'
              ? '📎 Fichier'
              : truncateMessage(lastMessage?.content)}
          </Typography>

          {/* Unread badge */}
          {unreadCount > 0 && (
            <Box
              sx={{
                backgroundColor: 'primary.main',
                color: 'white',
                borderRadius: '10px',
                minWidth: 20,
                height: 20,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.7rem',
                fontWeight: 700,
                ml: 1,
                px: 0.5,
              }}
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </Box>
          )}
        </Box>
      </Box>
    </ListItemButton>
  );
};

export default ConversationItem;
